import React from "react";

/** Shared pulse bone for admin skeletons */
export function Bone({ className = "" }) {
  return <div className={`animate-pulse rounded bg-gray-200 ${className}`} aria-hidden />;
}

export function TableSkeleton({ rows = 8, cols = 6, className = "" }) {
  return (
    <div className={`overflow-x-auto p-3 ${className}`} role="status" aria-label="Loading table">
      <div className="space-y-2">
        <div className="flex gap-2 mb-3">
          {Array.from({ length: cols }).map((_, i) => (
            <Bone key={`h-${i}`} className="h-3 flex-1" />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex gap-2 py-2.5 border-t border-gray-50">
            {Array.from({ length: cols }).map((_, c) => (
              <Bone key={`${r}-${c}`} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function CardsSkeleton({ count = 4, cols = "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" }) {
  return (
    <div className={`grid ${cols} gap-4`} role="status" aria-label="Loading cards">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
          <div className="flex justify-between gap-2">
            <Bone className="h-3 w-24" />
            <Bone className="h-4 w-14 rounded-full" />
          </div>
          <Bone className="h-7 w-20" />
          <Bone className="h-2 w-full" />
          <Bone className="h-2 w-2/3" />
        </div>
      ))}
    </div>
  );
}

export function ListSkeleton({ rows = 6 }) {
  return (
    <div className="space-y-3 p-3" role="status" aria-label="Loading list">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white p-3">
          <Bone className="h-10 w-10 rounded-full shrink-0" />
          <div className="flex-1 space-y-2">
            <Bone className="h-3 w-1/2" />
            <Bone className="h-2 w-3/4" />
          </div>
          <Bone className="h-6 w-16 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export function ChatSkeleton({ rows = 8 }) {
  return (
    <div className="space-y-3 p-4" role="status" aria-label="Loading chat">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={`flex ${i % 2 === 0 ? "justify-start" : "justify-end"}`}>
          <div className={`space-y-2 max-w-[70%] ${i % 2 === 0 ? "" : "items-end"}`}>
            <Bone className={`h-10 rounded-2xl ${i % 3 === 0 ? "w-48" : i % 3 === 1 ? "w-64" : "w-36"}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function FormSkeleton({ fields = 4 }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4" role="status" aria-label="Loading form">
      <Bone className="h-5 w-40" />
      <Bone className="h-3 w-64" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Array.from({ length: fields }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Bone className="h-3 w-24" />
            <Bone className="h-10 w-full" />
          </div>
        ))}
      </div>
      <Bone className="h-10 w-32" />
    </div>
  );
}

export function PageSkeleton({ showTabs = true, showCards = true, tableCols = 6, tableRows = 8 }) {
  return (
    <div className="p-4 md:p-6 space-y-5" role="status" aria-label="Loading page">
      <div className="space-y-2">
        <Bone className="h-7 w-56" />
        <Bone className="h-4 w-96 max-w-full" />
      </div>
      {showTabs && (
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Bone key={i} className="h-10 w-28 rounded-xl" />
          ))}
        </div>
      )}
      {showCards && <CardsSkeleton />}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex justify-between">
          <Bone className="h-5 w-40" />
          <Bone className="h-5 w-20" />
        </div>
        <TableSkeleton rows={tableRows} cols={tableCols} />
      </div>
    </div>
  );
}

export function PanelSkeleton({ className = "" }) {
  return (
    <div className={`rounded-xl border border-gray-200 bg-white p-5 space-y-4 ${className}`} role="status" aria-label="Loading">
      <Bone className="h-5 w-48" />
      <Bone className="h-3 w-72 max-w-full" />
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Bone key={i} className="h-10 w-full" />
        ))}
      </div>
    </div>
  );
}

export function InlineTableLoading({ rows = 8, cols = 6 }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <TableSkeleton rows={rows} cols={cols} />
    </div>
  );
}

/** Dark sidebar nav placeholder (matches black admin sidebar). */
export function SidebarSkeleton({ items = 12, className = "" }) {
  return (
    <div
      className={`lg:ml-8 px-2 ml-2 lg:px-4 w-full md:w-auto lg:w-90 h-full min-h-[calc(100vh-120px)] bg-black rounded-xl shadow-xl overflow-hidden text-white ${className}`}
      role="status"
      aria-label="Loading navigation"
    >
      <div className="flex flex-col mt-6 md:mt-10 gap-2 px-1">
        {Array.from({ length: items }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3">
            <div className="h-4 w-4 rounded animate-pulse bg-white/20 shrink-0" />
            <div
              className="h-3 rounded animate-pulse bg-white/20"
              style={{ width: `${52 + ((i * 17) % 36)}%` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Drop-in replacement for spinner + "Loading..." blocks in tables.
 */
export default function AdminSkeleton({
  variant = "table",
  rows,
  cols,
  count,
  className = "",
}) {
  if (variant === "page") return <PageSkeleton />;
  if (variant === "cards") return <CardsSkeleton count={count || 4} />;
  if (variant === "list") return <ListSkeleton rows={rows || 6} />;
  if (variant === "chat") return <ChatSkeleton rows={rows || 8} />;
  if (variant === "form") return <FormSkeleton fields={cols || 4} />;
  if (variant === "panel") return <PanelSkeleton className={className} />;
  if (variant === "sidebar") return <SidebarSkeleton items={rows || 12} className={className} />;
  if (variant === "modal") {
    return (
      <div className={`w-full p-4 ${className}`}>
        <TableSkeleton rows={rows || 5} cols={cols || 4} />
      </div>
    );
  }
  return <TableSkeleton rows={rows || 8} cols={cols || 6} className={className} />;
}
